import React, { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { FlaskConical, Pause, Play, RotateCcw, StepForward, Loader2 } from "lucide-react";
import { CATEGORY_PARTNERS, PERSPECTIVES, errMsg, pathForPerspective, perspectiveFromPath, useSSA } from "./SSAContext";

export default function DemoConsole() {
  const { call, catalog, category, setCategory, scenario, setScenario, activeRid, setActiveRid, bump } = useSSA();
  const visiblePersp = PERSPECTIVES.filter((p) => !p.pid || CATEGORY_PARTNERS[category].includes(p.pid));
  const loc = useLocation();
  const nav = useNavigate();
  const current = perspectiveFromPath(loc.pathname);
  const [busy, setBusy] = useState(false);
  const [auto, setAuto] = useState(false);
  const [next, setNext] = useState(null);
  const autoRef = useRef(false);

  useEffect(() => { autoRef.current = auto; }, [auto]);
  useEffect(() => {
    const m = loc.pathname.match(/\/partner\/([A-H])/) || loc.pathname.match(/UMS-(DM|FM)-/);
    if (!m) return;
    const cat = m[1].length === 2 ? m[1] : ("EFGH".includes(m[1]) ? "FM" : "DM");
    if (cat !== category) setCategory(cat);
  }, [loc.pathname, category, setCategory]);

  const step = async () => {
    setBusy(true);
    try {
      const r = await call("post", "/demo/step", { request_id: activeRid, service_id: scenario, country: "NL" });
      if (r.request_id) setActiveRid(r.request_id);
      if (r.performed) {
        toast.success(`${r.performed}`, { description: r.next ? `Next: ${r.next}` : "Scenario complete – full PO chain issued." });
        nav(pathForPerspective(r.performed_as, r.request_id, category));
      }
      if (r.done && !r.performed) toast("Scenario complete – press New to start another");
      setNext(r.done ? "Scenario complete – full PO chain issued. Press New for another run." : r.next || null);
      bump();
      return !r.done;
    } catch (e) {
      toast.error(errMsg(e));
      return false;
    } finally {
      setBusy(false);
    }
  };

  const runAuto = async () => {
    setAuto(true);
    autoRef.current = true;
    let more = true;
    while (more && autoRef.current) {
      more = await step();
      if (more) await new Promise((res) => setTimeout(res, 4500));
    }
    setAuto(false);
  };

  const reset = async () => {
    if (!window.confirm("Reset removes DEMO records only. Live requests stay untouched. Continue?")) return;
    const r = await call("post", "/demo/reset");
    setActiveRid(null);
    setNext(null);
    bump();
    toast.success(`Demo reset – ${r.deleted.requests} demo requests removed; live records untouched`);
    nav(`/services/c/${category}`);
  };

  const newScenario = () => { setActiveRid(null); setNext(null); toast("New scenario armed – press Next step"); };

  return (
    <div className="bg-[#1A1305] text-amber-50 border-b border-amber-500/30" data-testid="ssa-demo-console">
      <div className="max-w-[1500px] mx-auto px-4 lg:px-6 py-2 flex flex-wrap items-center gap-x-4 gap-y-2 text-xs">
        <span className="inline-flex items-center gap-1.5 font-bold uppercase tracking-[0.16em] text-amber-300">
          <FlaskConical size={13} /> Demo Console
        </span>
        <div className="flex items-center gap-1 bg-black/30 rounded-full p-0.5" role="tablist" aria-label="Demo perspective">
          {visiblePersp.map((p) => (
            <button
              key={p.key}
              role="tab"
              aria-selected={current === p.key}
              data-testid={`perspective-${p.key}`}
              onClick={() => nav(pathForPerspective(p.key, activeRid, category))}
              className={`px-3 py-1 rounded-full font-semibold transition-colors ${current === p.key ? "bg-amber-400 text-[#1A1305]" : "text-amber-100/80 hover:text-white hover:bg-white/10"}`}
            >
              {p.short}
            </button>
          ))}
        </div>
        <div className="flex items-center gap-2 ml-auto">
          <div className="flex bg-black/30 rounded-md p-0.5" role="tablist" aria-label="Demo category">
            {[["DM", "Digital Marketing"], ["FM", "Facilities (IFM)"]].map(([c, l]) => (
              <button key={c} role="tab" aria-selected={category === c} data-testid={`demo-category-${c}`}
                onClick={() => { if (c !== category) { setCategory(c); setActiveRid(null); setNext(null); nav(`/services/c/${c}`); } }}
                className={`px-2.5 py-1 rounded font-semibold ${category === c ? "bg-white text-[#1A1305]" : "text-amber-100/80 hover:text-white"}`}>{l}</button>
            ))}
          </div>
          <select
            value={scenario}
            onChange={(e) => { setScenario(e.target.value); newScenario(); }}
            data-testid="demo-scenario-select"
            className="bg-black/30 border border-amber-500/30 rounded-md px-2 py-1 text-amber-50 max-w-[220px]"
            aria-label="Demo scenario service"
          >
            {(catalog?.services || []).map((s) => <option key={s.id} value={s.id} className="text-slate-900">{s.id} · {s.name}</option>)}
          </select>
          {activeRid && <span className="font-mono text-amber-200/90" data-testid="demo-active-rid">{activeRid}</span>}
          <button onClick={newScenario} data-testid="demo-new-scenario" className="px-2 py-1 rounded-md border border-amber-500/30 hover:bg-white/10">New</button>
          <button onClick={step} disabled={busy || auto} data-testid="demo-next-step" className="inline-flex items-center gap-1 px-3 py-1 rounded-md bg-amber-400 text-[#1A1305] font-bold hover:bg-amber-300 disabled:opacity-60">
            {busy ? <Loader2 size={12} className="animate-spin" /> : <StepForward size={12} />} Next step
          </button>
          {auto ? (
            <button onClick={() => setAuto(false)} data-testid="demo-pause" className="inline-flex items-center gap-1 px-3 py-1 rounded-md border border-amber-400 text-amber-200"><Pause size={12} /> Pause</button>
          ) : (
            <button onClick={runAuto} disabled={busy} data-testid="demo-run-full" className="inline-flex items-center gap-1 px-3 py-1 rounded-md border border-amber-400 text-amber-200 hover:bg-white/10"><Play size={12} /> Run full demo</button>
          )}
          <button onClick={reset} data-testid="demo-reset" className="inline-flex items-center gap-1 px-2 py-1 rounded-md text-rose-300 hover:bg-rose-500/10"><RotateCcw size={12} /> Reset demo</button>
        </div>
        {next && <div className="w-full text-amber-200/80" data-testid="demo-next-hint">Paused · next: <span className="font-semibold text-amber-100">{next}</span></div>}
      </div>
    </div>
  );
}
